import { validateRuntimeAtlasCompatibility, type RuntimeAtlas } from './atlasContract.js';
import { ATLAS_CAP_BYTES } from './atlasBudget.js';
import type { AtlasLevel } from './levels.js';
import type { AtlasManifest } from './manifest.js';
import type { StagedRendererLevel, TycoonRendererAdapter } from './rendererAdapter.js';

export const DECODED_ATLAS_CAP_BYTES = ATLAS_CAP_BYTES;
export interface ClosableImage { close?: () => void }
export interface DecodedAtlasLevel {
  level: AtlasLevel;
  decodedBytes: number;
  atlas: RuntimeAtlas;
  pages: ClosableImage[];
  owned?: boolean;
  manifest?: AtlasManifest;
}
type Loader = (level: AtlasLevel, reserve: (decodedBytes: number) => void, manifest?: AtlasManifest) => Promise<DecodedAtlasLevel>;
class StagedReservationConflict extends Error {}

const release = (asset: DecodedAtlasLevel): void => { if (asset.owned !== false) asset.pages.forEach((page) => page.close?.()); };
const revisionOf=(asset:DecodedAtlasLevel):string|undefined=>asset.manifest?.sourceRevision??asset.atlas.sourceRevision;

export class AtlasLevelLifecycle {
  private generation = 0;
  private retained = new Map<AtlasLevel, DecodedAtlasLevel>();
  private pending?: { level: AtlasLevel; promise: Promise<boolean> };
  private reservations = new Map<number, number>();
  private reservationWaiters = new Set<() => void>();
  private nextRestorationToken = -1;
  private disposed = false;
  private restoringBaseline = false;
  private restoration?: Promise<void>;
  private readonly rendererStages = new WeakMap<DecodedAtlasLevel, StagedRendererLevel>();
  active?: DecodedAtlasLevel;
  retentionFailure?: Error;

  constructor(
    private readonly load: Loader,
    private readonly invalidateCaches: () => void,
    private readonly capBytes = DECODED_ATLAS_CAP_BYTES,
    private readonly renderer?: Pick<TycoonRendererAdapter, 'stageLevel' | 'activateLevel' | 'releaseLevel'>,
  ) {}

  private releaseAsset(asset: DecodedAtlasLevel): void {
    const staged=this.rendererStages.get(asset);
    if(staged){this.rendererStages.delete(asset);this.renderer?.releaseLevel(staged);}
    release(asset);
  }

  initialize(): Promise<boolean> { return this.request(1); }

  /** Invalidates an in-flight intent without initiating another decode. */
  supersedePending(): void {
    if (this.disposed) return;
    this.generation += 1;
    this.pending = undefined;
  }

  request(level: AtlasLevel): Promise<boolean> {
    if (this.disposed) return Promise.resolve(false);
    if (this.active?.level === level) {
      this.generation += 1;
      this.pending = undefined;
      return Promise.resolve(false);
    }
    if (level === 1 && this.restoration) return this.restoration.then(() => this.request(1));
    if (this.pending?.level === level) return this.pending.promise;
    const retained = this.retained.get(level);
    if (retained) {
      const generation=++this.generation;
      this.pending = undefined;
      const outgoing = this.active;
      const staged=this.rendererStages.get(retained);
      const activate=(handle:StagedRendererLevel|undefined):boolean=>{
        if(this.disposed||generation!==this.generation){if(handle&&!staged)this.renderer?.releaseLevel(handle);return false;}
        if(handle&&!staged)this.rendererStages.set(retained,handle);
        if(handle)this.renderer?.activateLevel(handle);
        this.active = retained;
        if (outgoing && outgoing.level !== 1 && outgoing !== retained) { this.retained.delete(outgoing.level); this.releaseAsset(outgoing); }
        this.invalidateCaches(); return true;
      };
      return staged ? Promise.resolve(activate(staged)) : this.renderer ? this.renderer.stageLevel(retained).then(activate).catch(error=>{this.retentionFailure=error instanceof Error?error:new Error(String(error));return false;}) : Promise.resolve(activate(undefined));
    }
    const generation = ++this.generation;
    const reserve = (decodedBytes: number): void => {
      if (!Number.isSafeInteger(decodedBytes) || decodedBytes < 0) throw new Error('invalid decoded atlas size');
      let residentBytes = [...new Set(this.retained.values())].reduce((sum, item) => sum + item.decodedBytes, 0);
      const stagedBytes = [...this.reservations.entries()].reduce((sum, [key, bytes]) => key === generation ? sum : sum + bytes, 0);
      const baseline = this.retained.get(1);
      if (residentBytes + stagedBytes + decodedBytes > this.capBytes && baseline && baseline !== this.active) {
        this.retained.delete(1);
        residentBytes -= baseline.decodedBytes;
        this.releaseAsset(baseline);
      }
      if (residentBytes + stagedBytes + decodedBytes > this.capBytes) {
        if (stagedBytes > 0 && residentBytes + decodedBytes <= this.capBytes) throw new StagedReservationConflict('decoded atlas staging reservation conflict');
        throw new Error('decoded atlas cap exceeded');
      }
      this.reservations.set(generation, decodedBytes);
    };
    const promise = this.load(level, reserve).then(async(candidate) => {
      if (this.disposed || generation !== this.generation) { this.releaseAsset(candidate); return false; }
      if (candidate.level !== level || candidate.atlas.level !== undefined && candidate.atlas.level !== level) { const error=new Error(`atlas candidate level ${candidate.level} does not match requested level ${level}`);this.retentionFailure=error;this.releaseAsset(candidate); return false; }
      try {
        if (candidate.atlas.level !== undefined) for (const resident of this.retained.values()) if (resident.atlas.level !== undefined&&revisionOf(resident)===revisionOf(candidate)) validateRuntimeAtlasCompatibility(resident.atlas, candidate.atlas);
      } catch(error) { this.retentionFailure=error instanceof Error?error:new Error(String(error));this.releaseAsset(candidate); return false; }
      const residentBytes = [...this.retained.values()].reduce((sum, item) => sum + item.decodedBytes, 0);
      const reserved = this.reservations.get(generation);
      if (!Number.isSafeInteger(candidate.decodedBytes) || candidate.decodedBytes < 0 || (reserved === undefined && residentBytes + candidate.decodedBytes > this.capBytes) || (reserved !== undefined && reserved !== candidate.decodedBytes)) {
        this.retentionFailure=new Error('atlas candidate decoded size does not match its reservation');this.releaseAsset(candidate); return false;
      }
      let rendererStage:StagedRendererLevel|undefined;
      try { rendererStage=await this.renderer?.stageLevel(candidate); }
      catch(error) { this.retentionFailure=error instanceof Error?error:new Error(String(error));this.releaseAsset(candidate);return false; }
      if(this.disposed||generation!==this.generation){if(rendererStage)this.renderer?.releaseLevel(rendererStage);this.releaseAsset(candidate);return false;}
      if(rendererStage)this.rendererStages.set(candidate,rendererStage);
      const outgoing = this.active;
      const revisionChanged=Boolean(outgoing&&revisionOf(outgoing)!==revisionOf(candidate));
      let transitionBaseline:DecodedAtlasLevel|undefined;
      if(revisionChanged&&level!==1){
        const transitionToken=this.nextRestorationToken--;
        try{
          transitionBaseline=await this.load(1,(decodedBytes)=>{if(!Number.isSafeInteger(decodedBytes)||decodedBytes<0)throw new Error('invalid decoded atlas size');const resident=[...new Set(this.retained.values())].reduce((sum,item)=>sum+item.decodedBytes,0),staged=[...this.reservations.values()].reduce((sum,bytes)=>sum+bytes,0);if(resident+staged+decodedBytes>this.capBytes)throw new Error('revision transition exceeds decoded atlas cap');this.reservations.set(transitionToken,decodedBytes);},candidate.manifest);
          if(this.disposed||generation!==this.generation)throw new Error('revision transition superseded');
          if(transitionBaseline.level!==1||(transitionBaseline.atlas.level!==undefined&&transitionBaseline.atlas.level!==1)||revisionOf(transitionBaseline)!==revisionOf(candidate))throw new Error('revision transition baseline mismatch');
          if(this.reservations.get(transitionToken)!==transitionBaseline.decodedBytes)throw new Error('revision transition baseline decoded size mismatch');
          const transitionBytes=[...new Set(this.retained.values())].reduce((sum,item)=>sum+item.decodedBytes,0)+candidate.decodedBytes+transitionBaseline.decodedBytes;
          if(transitionBytes>this.capBytes)throw new Error('revision transition exceeds decoded atlas cap');
          if(candidate.atlas.level!==undefined&&transitionBaseline.atlas.level!==undefined)validateRuntimeAtlasCompatibility(transitionBaseline.atlas,candidate.atlas);
          const baselineStage=await this.renderer?.stageLevel(transitionBaseline);
          if(this.disposed||generation!==this.generation){if(baselineStage)this.renderer?.releaseLevel(baselineStage);throw new Error('revision transition superseded');}
          if(baselineStage)this.rendererStages.set(transitionBaseline,baselineStage);
        }catch(error){if(transitionBaseline)this.releaseAsset(transitionBaseline);this.retentionFailure=error instanceof Error?error:new Error(String(error));this.releaseAsset(candidate);return false;}
        finally{this.reservations.delete(transitionToken);this.notifyReservationWaiters();}
      }
      if(rendererStage)this.renderer?.activateLevel(rendererStage);
      this.active = candidate;
      this.retained.set(level, candidate);
      if(revisionChanged){
        for(const [retainedLevel,asset] of [...this.retained])if(asset!==candidate){this.retained.delete(retainedLevel);this.releaseAsset(asset);}
        if(transitionBaseline)this.retained.set(1,transitionBaseline);
      }
      if (outgoing && outgoing.level !== 1 && outgoing.level !== level) {
        this.retained.delete(outgoing.level); if(!revisionChanged)this.releaseAsset(outgoing);
      }
      for (const [retainedLevel, asset] of [...this.retained]) {
        if (retainedLevel !== 1 && retainedLevel !== level) { this.retained.delete(retainedLevel); this.releaseAsset(asset); }
      }
      this.retentionFailure=undefined;
      this.invalidateCaches();
      return true;
    }).catch(async(error) => {
      if (error instanceof StagedReservationConflict && !this.disposed && generation === this.generation) {
        await this.waitForOtherReservations(generation);
        if (this.disposed || generation !== this.generation) return false;
        if (this.pending?.promise === promise) this.pending = undefined;
        return this.request(level);
      }
      this.retentionFailure=error instanceof Error?error:new Error(String(error));return false;
    }).finally(() => {
      this.reservations.delete(generation);
      this.notifyReservationWaiters();
      if (this.pending?.promise === promise) this.pending = undefined;
      if (!this.disposed && this.active?.level !== 1 && !this.retained.has(1) && !this.restoration) {
        this.restoration=this.restoreBaseline().finally(()=>{this.restoration=undefined;});
      }
    });
    this.pending = { level, promise };
    return promise;
  }

  private waitForOtherReservations(excluded: number): Promise<void> {
    if (![...this.reservations.keys()].some((key) => key !== excluded)) return Promise.resolve();
    return new Promise((resolve) => this.reservationWaiters.add(resolve));
  }

  private notifyReservationWaiters(): void {
    const waiters=[...this.reservationWaiters];
    this.reservationWaiters.clear();
    waiters.forEach((resolve)=>resolve());
  }

  private async restoreBaseline(): Promise<void> {
    if (this.restoringBaseline || this.disposed || this.active?.level === 1 || this.retained.has(1)) return;
    this.restoringBaseline = true;
    const active = this.active;
    const token=this.nextRestorationToken--;
    let candidate:DecodedAtlasLevel|undefined;
    try {
      candidate = await this.load(1, (decodedBytes) => {
        if (!Number.isSafeInteger(decodedBytes) || decodedBytes < 0) throw new Error('invalid decoded atlas size');
        const residentBytes = [...new Set(this.retained.values())].reduce((sum, item) => sum + item.decodedBytes, 0);
        const stagedBytes=[...this.reservations.values()].reduce((sum,bytes)=>sum+bytes,0);
        if (residentBytes + stagedBytes + decodedBytes > this.capBytes) throw new Error('inactive baseline cannot fit decoded atlas cap');
        this.reservations.set(token,decodedBytes);
      });
      if (this.disposed || !active || this.active !== active || candidate.level !== 1 || candidate.atlas.level !== undefined && candidate.atlas.level !== 1) { this.releaseAsset(candidate); return; }
      if(revisionOf(candidate)!==revisionOf(active))throw new Error('baseline restoration revision does not match active atlas');
      if (candidate.atlas.level !== undefined && active.atlas.level !== undefined) validateRuntimeAtlasCompatibility(active.atlas, candidate.atlas);
      const rendererStage=await this.renderer?.stageLevel(candidate);
      if(this.disposed||this.active!==active){if(rendererStage)this.renderer?.releaseLevel(rendererStage);this.releaseAsset(candidate);candidate=undefined;return;}
      if(rendererStage)this.rendererStages.set(candidate,rendererStage);
      this.retained.set(1, candidate);
      candidate=undefined;
      this.retentionFailure = undefined;
    } catch (error) {
      if(candidate)this.releaseAsset(candidate);
      this.retentionFailure = error instanceof Error ? error : new Error(String(error));
    } finally {
      this.reservations.delete(token);
      this.notifyReservationWaiters();
      this.restoringBaseline = false;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.generation += 1;
    for (const asset of this.retained.values()) this.releaseAsset(asset);
    this.retained.clear(); this.active = undefined; this.pending = undefined;
  }
}
